import { ConvergenceMatrix } from "../api";

function ActionPill({ action }: { action: string | null }) {
  if (action === null) return <span className="text-fg-muted">—</span>;
  if (action === "buy")
    return <span className="text-success font-bold uppercase">BUY</span>;
  if (action === "sell")
    return <span className="text-danger font-bold uppercase">SELL</span>;
  return <span className="text-warn uppercase">{action}</span>;
}

// Only strategies that apply to the symbol count towards agreement (F61).
function applicable(symbol: ConvergenceMatrix["symbols"][number]) {
  return symbol.strategies.filter((c) => c.applicable !== false);
}

function isAboutToFire(symbol: ConvergenceMatrix["symbols"][number]): boolean {
  const required = applicable(symbol);
  const filled = required.filter((c) => c.action !== null);
  if (filled.length < 2) return false;
  if (filled.length !== required.length) return false;
  const dirs = new Set(filled.map((c) => c.action));
  return dirs.size === 1;
}

export function ConvergenceMatrixPanel({
  matrix,
}: {
  matrix: ConvergenceMatrix | null;
}) {
  return (
    <section className="bg-surface border border-border rounded p-4 h-full">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Convergence Matrix
        </h2>
        <span className="text-xs text-fg-muted font-mono">
          {matrix ? `${matrix.mode}, ${matrix.window_seconds}s window` : "loading…"}
        </span>
      </div>

      {matrix && matrix.mode === "single" && (
        <p className="text-fg-muted text-sm">
          Single-fire mode: every buy/sell signal executes on its own. To
          require agreement between strategies, list their{" "}
          <code>strategy_id</code>s (comma-separated) in{" "}
          <strong>Trading-config → Convergence → Enabled strategies</strong>;
          the matrix then shows which ones have voted per symbol inside the
          window.
        </p>
      )}

      {matrix && matrix.mode === "strict" && (matrix.warnings?.length ?? 0) > 0 && (
        <div
          role="alert"
          className="mb-3 rounded border border-warn/50 bg-warn/10 px-3 py-2 text-sm text-warn"
        >
          <p className="font-bold">Convergence is one strategy on some symbols</p>
          <ul className="mt-1 list-disc pl-5">
            {matrix.warnings!.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {matrix && matrix.mode === "boost" && (
        <p className="text-fg-muted text-sm mb-2">
          Boost mode: each enabled strategy trades on its own; when two agree
          inside the window the entry is sized up (Trading-config →
          Convergence → Agreement size boost).
        </p>
      )}

      {matrix && matrix.mode !== "single" && matrix.symbols.length === 0 && (
        <p className="text-fg-muted text-sm">
          No in-flight signals on{" "}
          {matrix.enabled_strategies.length === 0
            ? "any strategy"
            : matrix.enabled_strategies.join(", ")}
          .
        </p>
      )}

      {matrix && matrix.symbols.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead className="text-xs uppercase text-fg-muted">
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3">Symbol</th>
                {matrix.enabled_strategies.map((sid) => (
                  <th key={sid} className="text-left py-2 pr-3">
                    {sid}
                  </th>
                ))}
                <th className="text-right py-2 pr-3">State</th>
              </tr>
            </thead>
            <tbody>
              {matrix.symbols.map((row) => {
                const willFire = isAboutToFire(row);
                return (
                  <tr
                    key={row.symbol}
                    className={
                      willFire
                        ? "border-b border-success/40 bg-success/10"
                        : "border-b border-border/60"
                    }
                  >
                    <td className="py-1 pr-3">{row.symbol}</td>
                    {row.strategies.map((c) => (
                      <td
                        key={c.strategy_id}
                        className={c.applicable === false ? "py-1 pr-3 opacity-40" : "py-1 pr-3"}
                        title={
                          c.applicable === false
                            ? `${c.strategy_id} is validated on another symbol; it does not count here`
                            : undefined
                        }
                      >
                        <div className="flex flex-col">
                          {c.applicable === false && c.action === null ? (
                            <span className="text-fg-muted">n/a</span>
                          ) : (
                            <ActionPill action={c.action} />
                          )}
                          {c.age_seconds !== null && (
                            <span className="text-xs text-fg-muted">
                              {c.age_seconds.toFixed(1)}s ago
                            </span>
                          )}
                        </div>
                      </td>
                    ))}
                    <td className="py-1 pr-3 text-right">
                      {willFire ? (
                        <span className="text-success font-bold uppercase">
                          all agreed
                        </span>
                      ) : (
                        <span className="text-fg-muted">waiting</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
